// ReinsuranceDeal detail screen.
//
// Full record view + inline edit. Hosts every per-deal panel: risk data,
// premium engine, reinsurer markets, sanctions banner, closings + document
// generation, endorsements, and the claims-validation link-back.
//
// Fetches: GET  /api/v1/reinsurance/deals/:id
// Saves:   PATCH /api/v1/reinsurance/deals/:id  (full body — the backend
//                                                whitelists forbidden fields)

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';
// One source for status wording — see STATUS_LABELS in kit.js. These selects
// rendered the raw stored value ("quoting"), so the control disagreed with
// every Pill AND with the dashboard, which calls the same state "In market".
import { statusLabel } from './kit';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  cardBg:  '#ffffff',
  soft:    '#f8fafc',
  burgundy:'#800020',
  accent:  '#0b3d5c',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '24px 40px', minHeight: 0 },
  head:    { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 4 },
  crumbs:  { fontSize: 12, color: C.muted, marginBottom: 6 },
  crumbLink: { color: C.muted, textDecoration: 'none', cursor: 'pointer' },
  dealNo:  { fontSize: 22, fontWeight: 700, color: C.ink },
  sub:     { color: C.muted, fontSize: 13, margin: '4px 0 20px' },

  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  btnDisabled: { padding: '9px 16px', borderRadius: 10, border: 'none', background: '#cbd5e1', color: '#fff', cursor: 'not-allowed', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  danger:  { padding: '9px 16px', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: 13 },

  card:    { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 16 },
  section: { fontSize: 12, fontWeight: 700, color: C.burgundy, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  grid:    { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  field:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:   { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6 },
  input:   { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, fontFamily: 'inherit' },
  ta:      { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, minHeight: 78, resize: 'vertical', fontFamily: 'inherit' },
  ro:      { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.soft, color: C.ink },

  pill:    (status) => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: status === 'bound' ? '#dcfce7' : status === 'closed' ? '#e2e8f0' : status === 'cancelled' ? '#fee2e2' : '#fef3c7',
    color:      status === 'bound' ? '#166534' : status === 'closed' ? '#475569' : status === 'cancelled' ? '#991b1b' : '#92400e',
  }),

  reinsTable: { width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 12 },
  reTh:       { textAlign: 'left', padding: '8px 10px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  reTd:       { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  reInput:    { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, background: '#fff', color: C.ink, fontFamily: 'inherit' },

  phase2Card: { background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 22, marginBottom: 16, color: C.muted },
  phase2H:    { fontSize: 13, fontWeight: 700, color: '#334155', margin: '0 0 6px' },
  phase2Sub:  { fontSize: 12, margin: '0 0 12px' },

  err:  { color: '#dc2626', fontSize: 13, marginTop: 8 },
  ok:   { color: '#166534', fontSize: 13, marginTop: 8 },
  meta: { fontSize: 11, color: C.muted },
};

const STATUS_OPTIONS = ['draft', 'submitted', 'quoting', 'bound', 'closed', 'cancelled'];
const REINS_STATUS   = ['pending', 'quoted', 'declined', 'written', 'signed', 'cancelled'];

const toDateInputValue = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0, 10); }
  catch (e) { return String(v).slice(0, 10); }
};

const fmt = (v) => (v == null || v === '' ? '—' : v);
const fmtMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return `${ccy || ''} ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim(); }
  catch (e) { return String(n); }
};
const fmtPct = (n) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 })}%`);
const fmtDateTime = (v) => {
  if (!v) return '—';
  try { const d = new Date(v); return `${d.toISOString().slice(0,10)} ${d.toISOString().slice(11,16)} UTC`; }
  catch (e) { return String(v); }
};

// Fields we send as Number when non-empty (mirrors backend NUMERIC_FIELDS
// in reinsuranceSubmissionController.js — keep in sync).
const NUMERIC_KEYS = [
  'sumInsured', 'premium',
  'retentionPct', 'cessionPct',
  'provisionalShare', 'cedingCommission', 'brokerage',
  'depositPremium', 'minimumPremium',
];

// Reinsurer row fields we also numeric-coerce on save.
const REINS_NUMERIC = ['provisionalShare', 'signedShare', 'premium', 'cedingCommission', 'brokerage'];

const emptyReinsurer = () => ({
  name: '', reference: '', currency: '',
  provisionalShare: '', signedShare: '', premium: '',
  cedingCommission: '', brokerage: '',
  status: 'pending', declineReason: '',
});

const DealDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const { confirm, ConfirmPortal } = useConfirm();

  const [deal, setDeal]           = useState(null);
  const [form, setForm]           = useState(null);   // editable copy
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveOk, setSaveOk]       = useState(false);
  const [error, setError]         = useState(null);
  const [calcBusy, setCalcBusy]   = useState(false);
  const [calcError, setCalcError] = useState(null);
  const [closingBusy, setClosingBusy] = useState(null);   // 'slip' | 'debit_note' | `credit_note:${idx}` | null
  const [closingError, setClosingError] = useState(null);
  const [claims, setClaims]           = useState([]);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [creatingClaim, setCreatingClaim] = useState(false);
  const [endorsements, setEndorsements] = useState([]);
  const [endsLoaded, setEndsLoaded]     = useState(false);
  const [creatingEnd, setCreatingEnd]   = useState(false);
  const [sanctions, setSanctions]         = useState(null);
  const [sanctionsBusy, setSanctionsBusy] = useState(null);
  // One note per pending party, keyed the same way sanctionsBusy is.
  const [attestNotes, setAttestNotes] = useState({});
  const [sanctionsError, setSanctionsError] = useState(null);

  // Module D tenant config — used to gate the sanctions banner + the
  // Premium Depth Terms card + the calc panel's depth extensions. All
  // absent-safe: a tenant with no config gets the pre-Module-D UI.
  const tenantConfig  = useSelector((s) => s.tenantConfig?.config);
  const featSanctions = !!tenantConfig?.features?.reinsuranceSanctions;
  const featDepth     = !!tenantConfig?.features?.reinsurancePremiumDepth;
  const depthFlags    = (featDepth && tenantConfig?.reinsurance?.premiumDepth) || {};

  const fetchDeal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/deals/${id}`, { withCredentials: true });
      const d = resp?.data?.data;
      if (!d) { setError('Deal not found'); return; }
      setDeal(d);
      setForm(hydrateForm(d));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load deal');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDeal(); }, [fetchDeal]);

  // Fetch claims tied to this deal. Best-effort — if the feature flag
  // is off on this tenant the API returns 403 and we render an empty
  // claims section rather than blowing up the whole page.
  const fetchClaims = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/claims?deal=${id}&limit=100`, { withCredentials: true });
      setClaims(resp?.data?.data || []);
    } catch (err) {
      // Silent when the tenant hasn't enabled reinsuranceClaimsValidation.
      setClaims([]);
    } finally {
      setClaimsLoaded(true);
    }
  }, [id]);
  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  // Endorsements — same best-effort pattern as claims.
  const fetchEndorsements = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/endorsements?deal=${id}&limit=100`, { withCredentials: true });
      setEndorsements(resp?.data?.data || []);
    } catch (err) {
      setEndorsements([]);
    } finally {
      setEndsLoaded(true);
    }
  }, [id]);
  useEffect(() => { fetchEndorsements(); }, [fetchEndorsements]);

  // Sanctions summary — only fetched when the tenant has the flag on.
  // Silent failure so a mis-configured tenant doesn't break the page.
  const fetchSanctions = useCallback(async () => {
    if (!featSanctions) { setSanctions(null); return; }
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/sanctions/deals/${id}/summary`, { withCredentials: true });
      setSanctions(resp?.data?.data || null);
    } catch (err) {
      setSanctions(null);
    }
  }, [id, featSanctions]);
  useEffect(() => { fetchSanctions(); }, [fetchSanctions]);

  // Screen one party (pending → adapter → row). Manual adapter creates a
  // pending row; reviewer attests via the same panel.
  const screenParty = async (p) => {
    setSanctionsBusy(`${p.subjectType}::${p.subjectName}`);
    setSanctionsError(null);
    try {
      await axios.post(`${BASE_URL}/reinsurance/sanctions/screen`, {
        subjectType:    p.subjectType,
        subjectName:    p.subjectName,
        deal:           id,
        dealNumber:     deal?.dealNumber,
        reinsurerIndex: p.reinsurerIndex,
        forceRescreen:  true,
      }, { withCredentials: true });
      await fetchSanctions();
    } catch (err) {
      setSanctionsError(err?.response?.data?.message || err.message || 'Screening failed');
    } finally { setSanctionsBusy(null); }
  };

  // Attest a pending row (manual mode). Quick actions on the banner:
  // "Attest clear" or "Attest flagged", with the note taken from the field
  // on the row rather than a native prompt — a prompt blocks the renderer
  // while it is open and tells the user nothing about what the note is for.
  const attestParty = async (p, newStatus) => {
    if (!p.checkId) return;
    const key = `${p.subjectType}::${p.subjectName}`;
    const attestation = (attestNotes[key] || '').trim();
    if (!attestation) {
      setSanctionsError('Add a note before attesting — the note IS the evidence.');
      return;
    }
    setSanctionsError('');
    setSanctionsBusy(key);
    try {
      const body = { status: newStatus, attestation };
      if (newStatus === 'flagged') body.hits = [{ name: p.subjectName, sanctionsList: 'manual attestation' }];
      await axios.post(`${BASE_URL}/reinsurance/sanctions/${p.checkId}/attest`, body, { withCredentials: true });
      setAttestNotes((prev) => ({ ...prev, [key]: '' }));
      await fetchSanctions();
    } catch (err) {
      setSanctionsError(err?.response?.data?.message || err.message || 'Attestation failed');
    } finally { setSanctionsBusy(null); }
  };

  // Quick-create a "draft" endorsement against this deal and jump to
  // the detail page for editing.
  const createEndorsement = async () => {
    setCreatingEnd(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const resp = await axios.post(`${BASE_URL}/reinsurance/endorsements`, {
        deal:          id,
        type:          'premium_adjustment',
        effectiveDate: today,
        currency:      deal.currency,
        premiumDelta:  0,
        status:        'draft',
      }, { withCredentials: true });
      const e = resp?.data?.data;
      if (e?._id) history.push(`/reinsurance/endorsements/${e._id}`);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create endorsement');
    } finally {
      setCreatingEnd(false);
    }
  };

  // Quick-create a "notified" claim shell against this deal, then jump
  // to the claim detail page for the reviewer to fill in.
  const createClaim = async () => {
    setCreatingClaim(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const resp = await axios.post(`${BASE_URL}/reinsurance/claims`, {
        deal:            id,
        dateOfLoss:      today,
        dateReported:    today,
        currency:        deal.currency,
        claimedAmount:   0,
        status:          'notified',
      }, { withCredentials: true });
      const c = resp?.data?.data;
      if (c?._id) history.push(`/reinsurance/claims/${c._id}`);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create claim');
    } finally {
      setCreatingClaim(false);
    }
  };

  const onField = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const onRef   = (k) => (e) => setForm((prev) => ({
    ...prev,
    referenceNumbers: { ...(prev.referenceNumbers || {}), [k]: e.target.value },
  }));
  const onReins = (idx, k) => (e) => setForm((prev) => {
    const next = { ...prev, reinsurers: [...(prev.reinsurers || [])] };
    next.reinsurers[idx] = { ...next.reinsurers[idx], [k]: e.target.value };
    return next;
  });
  const addReinsurer = () => setForm((prev) => ({
    ...prev,
    reinsurers: [...(prev.reinsurers || []), emptyReinsurer()],
  }));
  const removeReinsurer = (idx) => setForm((prev) => {
    const next = { ...prev, reinsurers: [...(prev.reinsurers || [])] };
    next.reinsurers.splice(idx, 1);
    return next;
  });

  const save = async () => {
    if (!form.cedantName || !String(form.cedantName).trim()) {
      setError('Cedant is required');
      return;
    }
    setSaving(true);
    setError(null);
    setSaveOk(false);
    try {
      const payload = buildPayload(form);
      const resp = await axios.patch(`${BASE_URL}/reinsurance/deals/${id}`, payload, { withCredentials: true });
      const d = resp?.data?.data;
      if (d) {
        setDeal(d);
        setForm(hydrateForm(d));
        setSaveOk(true);
        setTimeout(() => setSaveOk(false), 2000);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const calculatePremium = async () => {
    setCalcBusy(true);
    setCalcError(null);
    try {
      const resp = await axios.post(`${BASE_URL}/reinsurance/deals/${id}/calculate-premium`, {}, { withCredentials: true });
      const snapshot = resp?.data?.data;
      if (snapshot) {
        // Merge the snapshot back onto the deal so the panel re-renders
        // without a full refetch. computedBy comes back as an ObjectId
        // string; the panel doesn't display it directly.
        setDeal((prev) => ({ ...prev, calculatedPremium: snapshot }));
      }
    } catch (err) {
      setCalcError(err?.response?.data?.message || err.message || 'Calculation failed');
    } finally {
      setCalcBusy(false);
    }
  };

  // Closings — generate one of {slip, debit_note, credit_note} + refresh deal.
  // On success the deal's closings[] array grows; the list re-renders.
  const generateClosing = async (kind, extras = {}) => {
    const busyKey = kind === 'credit_note' ? `credit_note:${extras.reinsurerIndex}` : kind;
    setClosingBusy(busyKey);
    setClosingError(null);
    const path = kind === 'slip'
      ? 'slip'
      : kind === 'debit_note'
        ? 'debit-note'
        : 'credit-note';
    try {
      const resp = await axios.post(
        `${BASE_URL}/reinsurance/deals/${id}/closings/${path}`,
        kind === 'credit_note' ? { reinsurerIndex: extras.reinsurerIndex } : {},
        { withCredentials: true },
      );
      const row = resp?.data?.data;
      if (row) {
        setDeal((prev) => ({ ...prev, closings: [...(prev.closings || []), row] }));
      }
    } catch (err) {
      setClosingError(err?.response?.data?.message || err.message || 'Generation failed');
    } finally {
      setClosingBusy(null);
    }
  };

  const downloadClosing = async (closingId) => {
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/deals/${id}/closings/${closingId}/download`, { withCredentials: true });
      const url = resp?.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setClosingError(err?.response?.data?.message || err.message || 'Download link failed');
    }
  };

  const deleteDeal = async () => {
    const ok = await confirm({
      title: 'Delete reinsurance deal?',
      message: `${deal?.dealNumber || 'This deal'} will be permanently removed. This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/reinsurance/deals/${id}`, { withCredentials: true });
      history.push('/reinsurance/deals');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  if (loading) {
    return <AppLayout><div style={S.page}><div style={{ color: C.muted }}>Loading deal…</div></div></AppLayout>;
  }
  if (error && !deal) {
    return (
      <AppLayout>
        <div style={S.page}>
          <div style={S.crumbs}>
            <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
            <span> / {id}</span>
          </div>
          <div style={S.err}>{error}</div>
          <button style={{ ...S.ghost, marginTop: 12 }} onClick={() => history.push('/reinsurance/deals')}>← Back to deals</button>
        </div>
      </AppLayout>
    );
  }

  const currency = form.currency || deal.currency || '';

  return (
    <AppLayout>
      <div style={S.page}>
        {/* ── Crumbs + header ── */}
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
          <span> / {deal.dealNumber || id}</span>
        </div>
        <div style={S.head}>
          <div>
            <div style={S.dealNo}>
              {deal.dealNumber || '(no number)'} <span style={{ marginLeft: 10 }}><span style={S.pill(deal.status)}>{deal.status || 'draft'}</span></span>
            </div>
            <div style={S.meta}>
              Cedant: <b style={{ color: C.ink }}>{fmt(deal.cedantName)}</b>
              {deal.insuredName && <> · Insured: <b style={{ color: C.ink }}>{fmt(deal.insuredName)}</b></>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.ghost} onClick={() => history.push('/reinsurance/deals')}>← Back</button>
            <button style={S.danger} onClick={deleteDeal}>Delete</button>
            <button style={saving ? S.btnDisabled : S.btn} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
        <p style={S.sub}>Full record view. All fields editable. Premium engine, reinsurer markets, sanctions checks, closings, and endorsements are all managed here.</p>

        {/* ── Sanctions banner (tenant flag: reinsuranceSanctions) ── */}
        {featSanctions && sanctions && (
          <div style={{
            ...S.card,
            borderLeft: `4px solid ${
              sanctions.worst === 'flagged' ? '#dc2626' :
              sanctions.worst === 'error'   ? '#7f1d1d' :
              sanctions.worst === 'pending' ? '#d97706' :
              sanctions.worst === 'not_screened' ? '#94a3b8' : '#059669'
            }`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
              <div style={S.section}>Sanctions Screening</div>
              <button style={{ ...S.ghost, padding: '4px 10px', fontSize: 12 }} onClick={() => history.push('/reinsurance/sanctions')}>All screens →</button>
            </div>
            {sanctionsError && <div style={S.err}>{sanctionsError}</div>}
            <table style={S.reinsTable}>
              <thead>
                <tr>
                  <th style={S.reTh}>Party</th>
                  <th style={S.reTh}>Type</th>
                  <th style={S.reTh}>Status</th>
                  <th style={S.reTh}>Provider</th>
                  <th style={S.reTh}>Expires</th>
                  <th style={S.reTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sanctions.summary.map((p, i) => {
                  const key = `${p.subjectType}::${p.subjectName}`;
                  const busy = sanctionsBusy === key;
                  const pillStyle = {
                    display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: p.status === 'clear' ? '#dcfce7' : p.status === 'flagged' ? '#fee2e2' : p.status === 'error' ? '#fef2f2' : p.status === 'pending' ? '#fef3c7' : '#f1f5f9',
                    color:      p.status === 'clear' ? '#166534' : p.status === 'flagged' ? '#991b1b' : p.status === 'error' ? '#7f1d1d' : p.status === 'pending' ? '#92400e' : '#475569',
                  };
                  return (
                    <tr key={i}>
                      <td style={{ ...S.reTd, fontWeight: 600 }}>{p.subjectName}</td>
                      <td style={S.reTd}>{p.subjectType}</td>
                      <td style={S.reTd}><span style={pillStyle}>{(p.status || '').replace('_', ' ')}</span>{p.hitCount ? <span style={{ marginLeft: 6, fontSize: 10, color: C.muted }}>{p.hitCount} hit(s)</span> : null}</td>
                      <td style={S.reTd}>{p.provider || '—'}</td>
                      <td style={S.reTd}>{p.expiresAt ? new Date(p.expiresAt).toISOString().slice(0, 10) : '—'}</td>
                      <td style={S.reTd}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button style={{ ...S.ghost, padding: '4px 8px', fontSize: 11 }} disabled={busy} onClick={() => screenParty(p)}>
                            {busy ? '…' : (p.status === 'not_screened' ? 'Screen' : 'Re-screen')}
                          </button>
                          {p.status === 'pending' && (
                            <>
                              {/* The note is the evidence, so it is typed
                                  here, in front of the party it applies to —
                                  not into a native prompt that hides the row
                                  and blocks the page while it is open. */}
                              <input
                                value={attestNotes[`${p.subjectType}::${p.subjectName}`] || ''}
                                onChange={(e) => {
                                  const key = `${p.subjectType}::${p.subjectName}`;
                                  const { value } = e.target;
                                  setAttestNotes((prev) => ({ ...prev, [key]: value }));
                                }}
                                placeholder='Evidence source or reason'
                                aria-label={`Attestation note for ${p.subjectName}`}
                                style={{
                                  padding: '4px 8px', fontSize: 11, minWidth: 190,
                                  border: '1px solid #e5e7eb', borderRadius: 6,
                                  fontFamily: 'inherit', color: '#111827',
                                }}
                              />
                              <button style={{ ...S.btn, padding: '4px 8px', fontSize: 11 }} disabled={busy} onClick={() => attestParty(p, 'clear')}>Attest clear</button>
                              <button style={{ ...S.danger, padding: '4px 8px', fontSize: 11 }} disabled={busy} onClick={() => attestParty(p, 'flagged')}>Attest flagged</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Cedant / Insured ── */}
        <div style={S.card}>
          <div style={S.section}>Cedant & Insured</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Cedant *</label>
              <input style={S.input} value={form.cedantName || ''} onChange={onField('cedantName')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Insured Name</label>
              <input style={S.input} value={form.insuredName || ''} onChange={onField('insuredName')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Cedant Underwriter</label>
              <input style={S.input} value={form.referenceNumbers?.underwriter || ''} onChange={onRef('underwriter')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Status</label>
              <select style={S.input} value={form.status || 'draft'} onChange={onField('status')}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Risk ── */}
        <div style={S.card}>
          <div style={S.section}>Risk</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Line of Business</label>
              <input style={S.input} value={form.lineOfBusiness || ''} onChange={onField('lineOfBusiness')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Subclass</label>
              <input style={S.input} value={form.subclass || ''} onChange={onField('subclass')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Geography</label>
              <input style={S.input} value={form.geography || ''} onChange={onField('geography')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Occupation / Industry</label>
              <input style={S.input} value={form.occupation || ''} onChange={onField('occupation')} />
            </div>
          </div>
        </div>

        {/* ── Period & Financials ── */}
        <div style={S.card}>
          <div style={S.section}>Period & Financials</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Period Start</label>
              <input type='date' style={S.input} value={toDateInputValue(form.periodStart)} onChange={onField('periodStart')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Period End</label>
              <input type='date' style={S.input} value={toDateInputValue(form.periodEnd)} onChange={onField('periodEnd')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Currency (ISO)</label>
              <input style={S.input} value={form.currency || ''} onChange={onField('currency')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Sum Insured</label>
              <input type='number' style={S.input} value={form.sumInsured ?? ''} onChange={onField('sumInsured')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Original Premium</label>
              <input type='number' style={S.input} value={form.premium ?? ''} onChange={onField('premium')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Deposit Premium</label>
              <input type='number' style={S.input} value={form.depositPremium ?? ''} onChange={onField('depositPremium')} />
            </div>
            <div style={{ ...S.field, gridColumn: '1 / -1' }}>
              <label style={S.label}>Deductible (narrative)</label>
              <input style={S.input} value={form.deductible || ''} onChange={onField('deductible')} />
            </div>
          </div>
        </div>

        {/* ── Structure & Terms ── */}
        <div style={S.card}>
          <div style={S.section}>Reinsurance Structure — As Offered</div>
          <div style={S.grid}>
            <div style={{ ...S.field, gridColumn: '1 / -1' }}>
              <label style={S.label}>Structure</label>
              <input style={S.input} value={form.structureType || ''} onChange={onField('structureType')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Cedant Retention %</label>
              <input type='number' style={S.input} value={form.retentionPct ?? ''} onChange={onField('retentionPct')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Cession %</label>
              <input type='number' style={S.input} value={form.cessionPct ?? ''} onChange={onField('cessionPct')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Provisional Share %</label>
              <input type='number' style={S.input} value={form.provisionalShare ?? ''} onChange={onField('provisionalShare')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Ceding Commission %</label>
              <input type='number' style={S.input} value={form.cedingCommission ?? ''} onChange={onField('cedingCommission')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Brokerage %</label>
              <input type='number' style={S.input} value={form.brokerage ?? ''} onChange={onField('brokerage')} />
            </div>
          </div>
        </div>

        {/* ── Premium Depth Terms (tenant flag: reinsurancePremiumDepth) ── */}
        {featDepth && (
          <div style={S.card}>
            <div style={S.section}>Premium Depth Terms</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Optional per-tenant depth fields. Each sub-section renders only when the tenant has that specific capability enabled in TenantConfig.reinsurance.premiumDepth.
            </div>

            {depthFlags.minimumPremium && (
              <div style={{ ...S.field, marginBottom: 14 }}>
                <label style={S.label}>Minimum Premium (M&D)</label>
                <input type='number' style={S.input} value={form.minimumPremium ?? ''} onChange={onField('minimumPremium')} />
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Engine enforces max(computed, minimum). Cedant sees a note on the debit note when the minimum kicks in.</div>
              </div>
            )}

            {depthFlags.instalments && (
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Instalment Schedule</label>
                <div style={{ marginTop: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={S.reTh}>Due date</th>
                        <th style={S.reTh}>Amount</th>
                        <th style={S.reTh}>Description</th>
                        <th style={S.reTh}>Paid</th>
                        <th style={S.reTh}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.instalments || []).map((inst, i) => (
                        <tr key={i}>
                          <td style={S.reTd}><input type='date' style={S.reInput} value={inst.dueDate ? toDateInputValue(inst.dueDate) : ''} onChange={(e) => setForm((p) => { const next = [...p.instalments]; next[i] = { ...next[i], dueDate: e.target.value }; return { ...p, instalments: next }; })} /></td>
                          <td style={S.reTd}><input type='number' style={S.reInput} value={inst.amount ?? ''} onChange={(e) => setForm((p) => { const next = [...p.instalments]; next[i] = { ...next[i], amount: e.target.value }; return { ...p, instalments: next }; })} /></td>
                          <td style={S.reTd}><input style={S.reInput} value={inst.description || ''} onChange={(e) => setForm((p) => { const next = [...p.instalments]; next[i] = { ...next[i], description: e.target.value }; return { ...p, instalments: next }; })} placeholder='e.g. Q1'  /></td>
                          <td style={S.reTd}><input type='checkbox' checked={!!inst.paid} onChange={(e) => setForm((p) => { const next = [...p.instalments]; next[i] = { ...next[i], paid: e.target.checked }; return { ...p, instalments: next }; })} /></td>
                          <td style={S.reTd}><button style={{ ...S.ghost, padding: '4px 8px', fontSize: 11 }} onClick={() => setForm((p) => ({ ...p, instalments: p.instalments.filter((_, j) => j !== i) }))}>×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button style={{ ...S.ghost, marginTop: 6, fontSize: 12 }} onClick={() => setForm((p) => ({ ...p, instalments: [...(p.instalments || []), { dueDate: '', amount: '', description: '', paid: false }] }))}>+ Add instalment</button>
                </div>
              </div>
            )}

            {depthFlags.taxLines && (
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Tax Lines</label>
                <div style={{ marginTop: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={S.reTh}>Name</th>
                        <th style={S.reTh}>Rate %</th>
                        <th style={S.reTh}>Basis</th>
                        <th style={S.reTh}>Applies to</th>
                        <th style={S.reTh}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(form.taxLines || []).map((tx, i) => (
                        <tr key={i}>
                          <td style={S.reTd}><input style={S.reInput} value={tx.name || ''} onChange={(e) => setForm((p) => { const next = [...p.taxLines]; next[i] = { ...next[i], name: e.target.value }; return { ...p, taxLines: next }; })} placeholder='VAT / WHT / …' /></td>
                          <td style={S.reTd}><input type='number' step='0.01' style={S.reInput} value={tx.rate ?? ''} onChange={(e) => setForm((p) => { const next = [...p.taxLines]; next[i] = { ...next[i], rate: e.target.value }; return { ...p, taxLines: next }; })} /></td>
                          <td style={S.reTd}>
                            <select style={S.reInput} value={tx.basis || 'net_premium'} onChange={(e) => setForm((p) => { const next = [...p.taxLines]; next[i] = { ...next[i], basis: e.target.value }; return { ...p, taxLines: next }; })}>
                              <option value='net_premium'>net premium</option>
                              <option value='gross_premium'>gross premium</option>
                              <option value='ceded_premium'>ceded premium</option>
                              <option value='sub_total'>sub-total</option>
                            </select>
                          </td>
                          <td style={S.reTd}>
                            <select style={S.reInput} value={tx.appliesTo || 'cedant'} onChange={(e) => setForm((p) => { const next = [...p.taxLines]; next[i] = { ...next[i], appliesTo: e.target.value }; return { ...p, taxLines: next }; })}>
                              <option value='cedant'>cedant</option>
                              <option value='reinsurer'>reinsurer</option>
                              <option value='both'>both</option>
                            </select>
                          </td>
                          <td style={S.reTd}><button style={{ ...S.ghost, padding: '4px 8px', fontSize: 11 }} onClick={() => setForm((p) => ({ ...p, taxLines: p.taxLines.filter((_, j) => j !== i) }))}>×</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button style={{ ...S.ghost, marginTop: 6, fontSize: 12 }} onClick={() => setForm((p) => ({ ...p, taxLines: [...(p.taxLines || []), { name: '', rate: '', basis: 'net_premium', appliesTo: 'cedant' }] }))}>+ Add tax line</button>
                </div>
              </div>
            )}

            {(depthFlags.noClaimsBonus || depthFlags.profitCommission) && (
              <div style={S.grid}>
                {depthFlags.noClaimsBonus && (
                  <>
                    <div style={S.field}>
                      <label style={S.label}>NCB Rate %</label>
                      <input type='number' step='0.01' style={S.input} value={form.noClaimsBonus?.rate ?? ''} onChange={(e) => setForm((p) => ({ ...p, noClaimsBonus: { ...p.noClaimsBonus, rate: e.target.value } }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>NCB Loss-ratio threshold %</label>
                      <input type='number' step='0.01' style={S.input} value={form.noClaimsBonus?.threshold ?? ''} onChange={(e) => setForm((p) => ({ ...p, noClaimsBonus: { ...p.noClaimsBonus, threshold: e.target.value } }))} />
                    </div>
                  </>
                )}
                {depthFlags.profitCommission && (
                  <>
                    <div style={S.field}>
                      <label style={S.label}>Profit Commission Rate %</label>
                      <input type='number' step='0.01' style={S.input} value={form.profitCommission?.rate ?? ''} onChange={(e) => setForm((p) => ({ ...p, profitCommission: { ...p.profitCommission, rate: e.target.value } }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>PC Loss-ratio threshold %</label>
                      <input type='number' step='0.01' style={S.input} value={form.profitCommission?.threshold ?? ''} onChange={(e) => setForm((p) => ({ ...p, profitCommission: { ...p.profitCommission, threshold: e.target.value } }))} />
                    </div>
                    <div style={S.field}>
                      <label style={S.label}>PC base</label>
                      <select style={S.input} value={form.profitCommission?.base || 'net_premium'} onChange={(e) => setForm((p) => ({ ...p, profitCommission: { ...p.profitCommission, base: e.target.value } }))}>
                        <option value='net_premium'>net premium</option>
                        <option value='gross_premium'>gross premium</option>
                        <option value='ceded_premium'>ceded premium</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── References ── */}
        <div style={S.card}>
          <div style={S.section}>References</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Slip Reference</label>
              <input style={S.input} value={form.referenceNumbers?.slip || ''} onChange={onRef('slip')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Contract Reference</label>
              <input style={S.input} value={form.referenceNumbers?.contract || ''} onChange={onRef('contract')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Broker Reference</label>
              <input style={S.input} value={form.referenceNumbers?.broker || ''} onChange={onRef('broker')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Cedant Reference</label>
              <input style={S.input} value={form.referenceNumbers?.cedant || ''} onChange={onRef('cedant')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Co-Broker</label>
              <input style={S.input} value={form.coBroker || ''} onChange={onField('coBroker')} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Retrocedent</label>
              <input style={S.input} value={form.retrocedent || ''} onChange={onField('retrocedent')} />
            </div>
          </div>
        </div>

        {/* ── Reinsurers (embedded rows) ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <div style={S.section}>Reinsurer Markets — As Written</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...S.ghost, padding: '6px 12px', fontSize: 12 }} onClick={() => history.push(`/reinsurance/deals/${id}/placement`)}>
                Placement board →
              </button>
              <button style={S.ghost} onClick={addReinsurer}>+ Add market row</button>
            </div>
          </div>
          {form.reinsurers && form.reinsurers.length > 0 ? (
            <table style={S.reinsTable}>
              <thead>
                <tr>
                  <th style={S.reTh}>Reinsurer</th>
                  <th style={S.reTh}>Ref</th>
                  <th style={S.reTh}>Ccy</th>
                  <th style={S.reTh}>Prov %</th>
                  <th style={S.reTh}>Signed %</th>
                  <th style={S.reTh}>Premium</th>
                  <th style={S.reTh}>Ceding %</th>
                  <th style={S.reTh}>Brokerage %</th>
                  <th style={S.reTh}>Status</th>
                  <th style={S.reTh}></th>
                </tr>
              </thead>
              <tbody>
                {form.reinsurers.map((r, i) => (
                  <tr key={i}>
                    <td style={S.reTd}><input style={S.reInput} value={r.name || ''} onChange={onReins(i, 'name')} placeholder='Reinsurer name' /></td>
                    <td style={S.reTd}><input style={S.reInput} value={r.reference || ''} onChange={onReins(i, 'reference')} /></td>
                    <td style={S.reTd}><input style={{ ...S.reInput, width: 60 }} value={r.currency || ''} onChange={onReins(i, 'currency')} maxLength={3} /></td>
                    <td style={S.reTd}><input type='number' style={{ ...S.reInput, width: 70 }} value={r.provisionalShare ?? ''} onChange={onReins(i, 'provisionalShare')} /></td>
                    <td style={S.reTd}><input type='number' style={{ ...S.reInput, width: 70 }} value={r.signedShare ?? ''} onChange={onReins(i, 'signedShare')} /></td>
                    <td style={S.reTd}><input type='number' style={{ ...S.reInput, width: 110 }} value={r.premium ?? ''} onChange={onReins(i, 'premium')} /></td>
                    <td style={S.reTd}><input type='number' style={{ ...S.reInput, width: 70 }} value={r.cedingCommission ?? ''} onChange={onReins(i, 'cedingCommission')} /></td>
                    <td style={S.reTd}><input type='number' style={{ ...S.reInput, width: 70 }} value={r.brokerage ?? ''} onChange={onReins(i, 'brokerage')} /></td>
                    <td style={S.reTd}>
                      <select style={S.reInput} value={r.status || 'pending'} onChange={onReins(i, 'status')}>
                        {REINS_STATUS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                      </select>
                    </td>
                    <td style={S.reTd}>
                      <button style={{ ...S.ghost, padding: '4px 8px', fontSize: 12 }} onClick={() => removeReinsurer(i)}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              No markets yet. Add a row directly here, or use the Placement board to build capacity across reinsurers.
            </div>
          )}
        </div>

        {/* ── Terms & Notes ── */}
        <div style={S.card}>
          <div style={S.section}>Terms & Notes</div>
          <div style={{ ...S.field, marginBottom: 14 }}>
            <label style={S.label}>Terms</label>
            <textarea style={S.ta} value={form.terms || ''} onChange={onField('terms')} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Notes</label>
            <textarea style={S.ta} value={form.notes || ''} onChange={onField('notes')} />
          </div>
        </div>

        {/* ── Premium Engine ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={S.section}>Premium Engine</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {deal.calculatedPremium?.computedAt && (
                <span style={S.meta}>Last calc: {fmtDateTime(deal.calculatedPremium.computedAt)}</span>
              )}
              <button style={calcBusy ? S.btnDisabled : S.btn} onClick={calculatePremium} disabled={calcBusy}>
                {calcBusy ? 'Calculating…' : (deal.calculatedPremium?.computedAt ? 'Recalculate premium' : 'Calculate premium')}
              </button>
            </div>
          </div>

          {calcError && <div style={S.err}>{calcError}</div>}

          {!deal.calculatedPremium?.computedAt && !calcError && (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              Not calculated yet. Click <b>Calculate premium</b> to run the engine — it uses the deal's original premium,
              cession %, and the reinsurers[] rows above to compute per-market amounts, ceding commission, brokerage, and
              net-to-reinsurer figures. Result is snapshotted on the deal for closings to consume.
            </div>
          )}

          {deal.calculatedPremium?.computedAt && (
            <>
              {/* Warnings first — non-blocking but visible */}
              {deal.calculatedPremium.warnings && deal.calculatedPremium.warnings.length > 0 && (
                <div style={{ marginBottom: 12, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Warnings ({deal.calculatedPremium.warnings.length})</div>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {deal.calculatedPremium.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Deal-level totals */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Deal Totals</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  {[
                    ['Original premium', deal.calculatedPremium.dealTotals?.originalPremium],
                    ['Retained by cedant', deal.calculatedPremium.dealTotals?.retainedPremium],
                    ['Ceded to market', deal.calculatedPremium.dealTotals?.cededPremium],
                    ['Total placed', deal.calculatedPremium.dealTotals?.totalMarketPremium],
                    ['Unplaced cession', deal.calculatedPremium.dealTotals?.unplacedCededPremium],
                    ['Ceding commission (to cedant)', deal.calculatedPremium.dealTotals?.totalCedingCommission],
                    ['Brokerage', deal.calculatedPremium.dealTotals?.totalBrokerage],
                    ['Net to reinsurers', deal.calculatedPremium.dealTotals?.totalNetToReinsurers],
                  ].map(([label, val], i) => (
                    <div key={i} style={{ padding: '10px 12px', background: C.soft, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{fmtMoney(val, deal.calculatedPremium.dealTotals?.currency)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Per-market breakdown */}
              {deal.calculatedPremium.byMarket && deal.calculatedPremium.byMarket.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Per Market</div>
                  <table style={S.reinsTable}>
                    <thead>
                      <tr>
                        <th style={S.reTh}>Reinsurer</th>
                        <th style={S.reTh}>Share</th>
                        <th style={S.reTh}>Premium</th>
                        <th style={S.reTh}>Ceding %</th>
                        <th style={S.reTh}>Ceding amt</th>
                        <th style={S.reTh}>Brokerage %</th>
                        <th style={S.reTh}>Brokerage amt</th>
                        <th style={S.reTh}>Net to reinsurer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deal.calculatedPremium.byMarket.map((m, i) => {
                        // Amounts are stored in DEAL currency by the engine;
                        // `m.currency` is the SETTLEMENT currency for FX at
                        // closings. Previously we labeled amounts with
                        // m.currency, which showed "EUR 900,000" for an
                        // unconverted CHF figure (Test Run 7-22 L1).
                        const dealCcy       = m.dealCurrency || deal.calculatedPremium.dealTotals?.currency || deal.currency;
                        const settlementCcy = m.currency || dealCcy;
                        const settlesIn     = settlementCcy && settlementCcy !== dealCcy ? ` (settles in ${settlementCcy})` : '';
                        return (
                          <tr key={i}>
                            <td style={S.reTd}>
                              <div style={{ fontWeight: 600, color: C.ink }}>{m.reinsurerName}{settlesIn && <span style={{ color: C.muted, fontWeight: 400 }}>{settlesIn}</span>}</div>
                              {m.shareBasis === 'provisional' && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>using provisional share (signed share not set)</div>}
                            </td>
                            <td style={S.reTd}>{fmtPct(m.share)}</td>
                            <td style={S.reTd}>{fmtMoney(m.premium, dealCcy)}</td>
                            <td style={S.reTd}>{fmtPct(m.cedingCommissionPct)}</td>
                            <td style={S.reTd}>{fmtMoney(m.cedingCommission, dealCcy)}</td>
                            <td style={S.reTd}>{fmtPct(m.brokeragePct)}</td>
                            <td style={S.reTd}>{fmtMoney(m.brokerage, dealCcy)}</td>
                            <td style={{ ...S.reTd, fontWeight: 600 }}>{fmtMoney(m.netToReinsurer, dealCcy)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}

              {/* Premium Depth outputs — tenant flag gated, present only
                  when the engine produced a premiumDepth block. */}
              {featDepth && deal.calculatedPremium?.premiumDepth && (
                <>
                  {deal.calculatedPremium.premiumDepth.minimumApplied?.enforced && (
                    <div style={{ marginTop: 14, padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 12 }}>
                      Minimum premium enforced — computed {fmtMoney(deal.calculatedPremium.premiumDepth.minimumApplied.computed, deal.calculatedPremium.dealTotals?.currency)} was below minimum {fmtMoney(deal.calculatedPremium.premiumDepth.minimumApplied.minimum, deal.calculatedPremium.dealTotals?.currency)}. Applied: {fmtMoney(deal.calculatedPremium.premiumDepth.minimumApplied.applied, deal.calculatedPremium.dealTotals?.currency)}.
                    </div>
                  )}
                  {Array.isArray(deal.calculatedPremium.premiumDepth.taxLines) && deal.calculatedPremium.premiumDepth.taxLines.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Tax Lines</div>
                      <table style={S.reinsTable}>
                        <thead>
                          <tr>
                            <th style={S.reTh}>Name</th>
                            <th style={S.reTh}>Rate</th>
                            <th style={S.reTh}>Basis</th>
                            <th style={S.reTh}>Base amount</th>
                            <th style={S.reTh}>Tax amount</th>
                            <th style={S.reTh}>Applies to</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deal.calculatedPremium.premiumDepth.taxLines.map((t, i) => (
                            <tr key={i}>
                              <td style={{ ...S.reTd, fontWeight: 600 }}>{t.name}</td>
                              <td style={S.reTd}>{fmtPct(t.rate)}</td>
                              <td style={S.reTd}>{(t.basis || '').replace('_', ' ')}</td>
                              <td style={S.reTd}>{fmtMoney(t.base, deal.calculatedPremium.dealTotals?.currency)}</td>
                              <td style={{ ...S.reTd, fontWeight: 600 }}>{fmtMoney(t.amount, deal.calculatedPremium.dealTotals?.currency)}</td>
                              <td style={S.reTd}>{t.appliesTo}</td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={4} style={{ ...S.reTd, fontWeight: 700, textAlign: 'right' }}>Total taxes</td>
                            <td style={{ ...S.reTd, fontWeight: 700 }}>{fmtMoney(deal.calculatedPremium.premiumDepth.totalTaxes, deal.calculatedPremium.dealTotals?.currency)}</td>
                            <td style={S.reTd} />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {Array.isArray(deal.calculatedPremium.premiumDepth.instalments) && deal.calculatedPremium.premiumDepth.instalments.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Instalment Schedule</div>
                      <table style={S.reinsTable}>
                        <thead><tr><th style={S.reTh}>Due</th><th style={S.reTh}>Amount</th><th style={S.reTh}>Description</th><th style={S.reTh}>Status</th></tr></thead>
                        <tbody>
                          {deal.calculatedPremium.premiumDepth.instalments.map((i, idx) => (
                            <tr key={idx}>
                              <td style={S.reTd}>{i.dueDate ? toDateInputValue(i.dueDate) : '—'}</td>
                              <td style={S.reTd}>{fmtMoney(i.amount, i.currency)}</td>
                              <td style={S.reTd}>{i.description || '—'}</td>
                              <td style={S.reTd}>{i.paid ? 'paid' : 'due'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {Math.abs(deal.calculatedPremium.premiumDepth.instalmentsBalance || 0) > 0.01 && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>
                          Schedule differs from premium by {fmtMoney(deal.calculatedPremium.premiumDepth.instalmentsBalance, deal.calculatedPremium.dealTotals?.currency)}.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ── Closings & Documents ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={S.section}>Closings & Documents</div>
            <button style={{ ...S.ghost, padding: '4px 10px', fontSize: 12 }} onClick={() => history.push('/reinsurance/fx-rates')}>
              Manage FX rates →
            </button>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Tenant-branded PDFs from the deal + premium engine snapshot. Slip works from raw deal data; debit + credit notes require
            a Premium Engine calc. Credit notes convert to the reinsurer's currency automatically when a matching FX rate is on file.
            Each generation appends to the history below and audit-logs to the tenant's trail.
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <button
              style={closingBusy === 'slip' ? S.btnDisabled : S.btn}
              disabled={!!closingBusy}
              onClick={() => generateClosing('slip')}
            >
              {closingBusy === 'slip' ? 'Generating…' : 'Generate slip'}
            </button>
            <button
              style={closingBusy === 'debit_note' || !deal.calculatedPremium?.computedAt ? S.btnDisabled : S.btn}
              disabled={!!closingBusy || !deal.calculatedPremium?.computedAt}
              onClick={() => generateClosing('debit_note')}
              title={!deal.calculatedPremium?.computedAt ? 'Run the Premium Engine first' : 'Generate debit note to cedant'}
            >
              {closingBusy === 'debit_note' ? 'Generating…' : 'Generate debit note (to cedant)'}
            </button>
          </div>

          {/* Per-reinsurer credit notes — one button per market. Only shown
              when the engine has produced a byMarket breakdown. */}
          {deal.calculatedPremium?.byMarket && deal.calculatedPremium.byMarket.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Credit notes per market</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {deal.calculatedPremium.byMarket.map((m, i) => {
                  const busy = closingBusy === `credit_note:${i}`;
                  return (
                    <button
                      key={i}
                      style={busy ? S.btnDisabled : S.ghost}
                      disabled={!!closingBusy}
                      onClick={() => generateClosing('credit_note', { reinsurerIndex: i })}
                    >
                      {busy ? 'Generating…' : `Credit note → ${m.reinsurerName}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {closingError && <div style={S.err}>{closingError}</div>}

          {/* Closings history — most recent first */}
          {deal.closings && deal.closings.length > 0 ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, margin: '14px 0 6px' }}>Generated closings</div>
              <table style={S.reinsTable}>
                <thead>
                  <tr>
                    <th style={S.reTh}>Ref</th>
                    <th style={S.reTh}>Kind</th>
                    <th style={S.reTh}>Reinsurer</th>
                    <th style={S.reTh}>Filename</th>
                    <th style={S.reTh}>Generated</th>
                    <th style={S.reTh}></th>
                  </tr>
                </thead>
                <tbody>
                  {[...deal.closings].reverse().map((c) => (
                    <tr key={c._id}>
                      <td style={{ ...S.reTd, fontWeight: 600, color: C.burgundy }}>{c.ref || '—'}</td>
                      <td style={S.reTd}>{(c.kind || '').replace('_', ' ')}</td>
                      <td style={S.reTd}>{c.reinsurerName || '—'}</td>
                      <td style={S.reTd}>{c.filename || '—'}</td>
                      <td style={S.reTd}>{fmtDateTime(c.generatedAt)}</td>
                      <td style={S.reTd}>
                        <button style={{ ...S.ghost, padding: '4px 10px', fontSize: 12 }} onClick={() => downloadClosing(c._id)}>Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{ padding: 14, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 12 }}>
              No closings generated yet.
            </div>
          )}
        </div>

        {/* ── Endorsements ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={S.section}>Endorsements</div>
            <button style={creatingEnd ? S.btnDisabled : S.btn} onClick={createEndorsement} disabled={creatingEnd}>
              {creatingEnd ? 'Creating…' : '+ New endorsement'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Mid-term changes against this deal — premium adjustments, sum-insured changes, period extensions, cancellations. Each carries its own impact recalc + endorsement closings.
          </div>
          {endsLoaded && endorsements.length === 0 ? (
            <div style={{ padding: 14, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 12 }}>
              No endorsements on this deal yet.
            </div>
          ) : (
            <table style={S.reinsTable}>
              <thead>
                <tr>
                  <th style={S.reTh}>Endorsement #</th>
                  <th style={S.reTh}>Type</th>
                  <th style={S.reTh}>Effective</th>
                  <th style={S.reTh}>Premium Δ</th>
                  <th style={S.reTh}>Status</th>
                </tr>
              </thead>
              <tbody>
                {endorsements.map((e) => (
                  <tr
                    key={e._id}
                    onClick={() => history.push(`/reinsurance/endorsements/${e._id}`)}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = C.soft; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...S.reTd, fontWeight: 600, color: C.burgundy }}>{e.endorsementNumber}</td>
                    <td style={S.reTd}>{(e.type || '').replace(/_/g, ' ')}</td>
                    <td style={S.reTd}>{toDateInputValue(e.effectiveDate)}</td>
                    <td style={{ ...S.reTd, color: (e.premiumDelta || 0) > 0 ? '#065f46' : (e.premiumDelta || 0) < 0 ? '#991b1b' : C.muted }}>
                      {e.premiumDelta != null ? `${e.premiumDelta > 0 ? '+' : ''}${e.currency || ''} ${Number(e.premiumDelta).toLocaleString('en-US')}` : '—'}
                    </td>
                    <td style={S.reTd}>{e.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Claims ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={S.section}>Claims</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...S.ghost, padding: '6px 12px', fontSize: 12 }} onClick={() => history.push(`/reinsurance/claims?deal=${id}`)}>View all →</button>
              <button style={creatingClaim ? S.btnDisabled : S.btn} onClick={createClaim} disabled={creatingClaim}>
                {creatingClaim ? 'Creating…' : '+ New claim'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
            Losses reported against this deal. Each claim carries its own validation snapshot (period-in-cover check, market allocation) and claim closings.
          </div>
          {claimsLoaded && claims.length === 0 ? (
            <div style={{ padding: 14, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 12 }}>
              No claims on this deal yet.
            </div>
          ) : (
            <table style={S.reinsTable}>
              <thead>
                <tr>
                  <th style={S.reTh}>Claim #</th>
                  <th style={S.reTh}>Cedant Ref</th>
                  <th style={S.reTh}>Date of Loss</th>
                  <th style={S.reTh}>Claimed</th>
                  <th style={S.reTh}>Status</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => (
                  <tr
                    key={c._id}
                    onClick={() => history.push(`/reinsurance/claims/${c._id}`)}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.soft; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...S.reTd, fontWeight: 600, color: C.burgundy }}>{c.claimNumber}</td>
                    <td style={S.reTd}>{c.claimReference || '—'}</td>
                    <td style={S.reTd}>{toDateInputValue(c.dateOfLoss)}</td>
                    <td style={S.reTd}>{c.currency || ''} {Number(c.claimedAmount || 0).toLocaleString('en-US')}</td>
                    <td style={S.reTd}>{(c.status || '').replace('_', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Provenance ── */}
        <div style={S.card}>
          <div style={S.section}>Provenance</div>
          <div style={S.grid}>
            <div style={S.field}>
              <label style={S.label}>Source Document</label>
              <div style={S.ro}>
                {deal.sourceDocument?.fileExchangeId
                  ? <>File {String(deal.sourceDocument.fileExchangeId).slice(-8)} — extracted {toDateInputValue(deal.sourceDocument.extractedAt)}{deal.sourceDocument.confidence != null ? ` · ${(deal.sourceDocument.confidence * 100).toFixed(0)}% confidence` : ''}</>
                  : 'Manual entry'}
              </div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Currency (base)</label>
              <div style={S.ro}>{currency || '—'}</div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Created</label>
              <div style={S.ro}>{fmt(toDateInputValue(deal.createdAt))}</div>
            </div>
            <div style={S.field}>
              <label style={S.label}>Last updated</label>
              <div style={S.ro}>{fmt(toDateInputValue(deal.updatedAt))}</div>
            </div>
          </div>
        </div>

        {error && <div style={S.err}>{error}</div>}
        {saveOk && <div style={S.ok}>Saved.</div>}
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

// Copy a deal into a mutable form shape. Dates become YYYY-MM-DD strings
// for <input type='date'>; the rest is passed as-is so reinsurers[] etc.
// remain editable arrays/objects.
function hydrateForm(d) {
  return {
    cedantName:      d.cedantName || '',
    insuredName:     d.insuredName || '',
    lineOfBusiness:  d.lineOfBusiness || '',
    subclass:        d.subclass || '',
    geography:       d.geography || '',
    occupation:      d.occupation || '',
    currency:        d.currency || '',
    structureType:   d.structureType || '',
    sumInsured:      d.sumInsured ?? '',
    premium:         d.premium ?? '',
    deductible:      d.deductible || '',
    retentionPct:    d.retentionPct ?? '',
    cessionPct:      d.cessionPct ?? '',
    provisionalShare:d.provisionalShare ?? '',
    cedingCommission:d.cedingCommission ?? '',
    brokerage:       d.brokerage ?? '',
    depositPremium:  d.depositPremium ?? '',
    minimumPremium:  d.minimumPremium ?? '',
    periodStart:     d.periodStart || '',
    periodEnd:       d.periodEnd || '',
    coBroker:        d.coBroker || '',
    retrocedent:     d.retrocedent || '',
    referenceNumbers: { ...(d.referenceNumbers || {}) },
    reinsurers:      Array.isArray(d.reinsurers) ? d.reinsurers.map((r) => ({ ...r })) : [],
    // Module D — premium depth fields (all optional; only surfaced on
    // the form when the tenant flag is on).
    instalments:     Array.isArray(d.instalments) ? d.instalments.map((i) => ({ ...i })) : [],
    taxLines:        Array.isArray(d.taxLines)    ? d.taxLines.map((t) => ({ ...t }))    : [],
    noClaimsBonus:   { ...(d.noClaimsBonus || { rate: '', threshold: '' }) },
    profitCommission:{ ...(d.profitCommission || { rate: '', threshold: '', base: 'net_premium' }) },
    terms:           d.terms || '',
    notes:           d.notes || '',
    status:          d.status || 'draft',
  };
}

// Build the PATCH body: coerce numerics, strip empty strings, keep
// referenceNumbers + reinsurers as nested objects/arrays. Backend
// createDeal / updateDeal already reject server-managed fields, so we
// don't need to filter _id/org/dealNumber here.
function buildPayload(form) {
  const out = { ...form };
  NUMERIC_KEYS.forEach((k) => {
    if (out[k] === '' || out[k] == null) delete out[k];
    else out[k] = Number(out[k]);
  });
  if (Array.isArray(out.reinsurers)) {
    out.reinsurers = out.reinsurers.map((r) => {
      const row = { ...r };
      REINS_NUMERIC.forEach((k) => {
        if (row[k] === '' || row[k] == null) delete row[k];
        else row[k] = Number(row[k]);
      });
      return row;
    });
  }
  // Module D — coerce nested premium-depth numerics + drop empty rows.
  if (Array.isArray(out.instalments)) {
    out.instalments = out.instalments
      .map((i) => {
        const row = { ...i };
        if (row.amount === '' || row.amount == null) return null;
        row.amount = Number(row.amount);
        return row;
      })
      .filter(Boolean);
    if (out.instalments.length === 0) delete out.instalments;
  }
  if (Array.isArray(out.taxLines)) {
    out.taxLines = out.taxLines
      .map((t) => {
        const row = { ...t };
        if (!row.name || row.rate === '' || row.rate == null) return null;
        row.rate = Number(row.rate);
        return row;
      })
      .filter(Boolean);
    if (out.taxLines.length === 0) delete out.taxLines;
  }
  ['noClaimsBonus', 'profitCommission'].forEach((k) => {
    if (out[k]) {
      const row = { ...out[k] };
      ['rate', 'threshold'].forEach((n) => {
        if (row[n] === '' || row[n] == null) delete row[n];
        else row[n] = Number(row[n]);
      });
      // Drop the whole sub-object if empty.
      if (row.rate == null && row.threshold == null) delete out[k];
      else out[k] = row;
    }
  });
  return out;
}

export default DealDetail;
